import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  // Divider,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OTPVerification from "components/general/auth/OTPVerification";
import SignInOptions from "components/general/auth/SignInOptions";
import { FORM_VALIDATIONS } from "constants/formValidation";
import { OTP_TYPE, REGISTRATION_TYPES } from "constants/general";
import { ROUTES } from "constants/routes";
import { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  setIsSubmitting,
  setOtpTokenData,
  setOtpVerification,
  setSignInFormError,
  signInUserAction,
} from "slices/authReducers";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import AuthPagesDescription from "components/AuthPagesDescription";

import { CustomTextField } from "components/mui-common/CustomTextField";
import { useLocation, useNavigate } from "react-router-dom";
import useQuery from "utils/router.util";
import { LoginTitle, RootBox } from "./mui.styles";
import CryptoJS from "crypto-js";

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useTheme();

  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm({
    reValidateMode: "onChange",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  let query: any = useQuery();
  const redirectPath = query.get("redirect");
  const location = useLocation();

  const { authReducer } = useAppSelector((state) => state);
  const { isSubmitting, signInFormError, otpVerification } = authReducer;

  const secretKey: any = process.env.REACT_APP_AUTH_CRED_STORAGE_ENCRYPTION_KEY;
  useEffect(() => {
    const storedUsername = localStorage.getItem("email");
    const storedPassword = localStorage.getItem("password");
    if (storedUsername && storedPassword) {
      const decryptedPassword = CryptoJS.AES.decrypt(
        storedPassword,
        secretKey
      ).toString(CryptoJS.enc.Utf8);

      setValue("email", storedUsername);
      setValue("password", decryptedPassword);

      trigger(["email", "password"]);
    }
    dispatch(setIsSubmitting(false));
  }, []);

  useEffect(() => {
    dispatch(setSignInFormError(""));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setOtpVerification(false));
      dispatch(setOtpTokenData({}));
    };
  }, [dispatch]);

  /**
   * @method: Method to submit Login form
   */
  const doLogin = async (data: any) => {
    dispatch(
      signInUserAction({
        data: { ...data, registration_type: REGISTRATION_TYPES.EMAIL },
        navigate: () => {
          // auth routes
          const authRoutes = [ROUTES.SIGNIN, ROUTES.SIGNUP];
          // if user is logged in and land on auth routes then redirect to home page
          if (authRoutes.includes(location?.pathname)) {
            if (redirectPath) {
              navigate(redirectPath);
            } else {
              navigate(ROUTES.HOME);
            }
            if (rememberMe) {
              const encryptedPassword = CryptoJS.AES.encrypt(
                data?.password,
                secretKey
              ).toString();
              localStorage.setItem("email", data?.email);
              localStorage.setItem("password", encryptedPassword);
            }
          }
        },
      })
    );
  };

  /**
   * @method: Method to redirect user to Signup page
   */
  const goToSignUp = () => {
    if (query?.redirect) {
      navigate(`${ROUTES.SIGNUP}?redirect=${query?.redirect}`);
    } else {
      navigate(ROUTES.SIGNUP);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: isMobileView ? "transparent" : "primary.main",
        minHeight: isMobileView ? "100%" : "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={8}
        sx={{ width: "100%", height: "100vh" }}
      >
        {!isMobileView && (
          <Box sx={{ width: 450 }}>
            <AuthPagesDescription />
          </Box>
        )}
        <RootBox
          sx={{
            maxWidth: isMobileView ? "100%" : "384px",
            ...(isMobileView && { width: "100%", height: "100%" }),
          }}
        >
          {otpVerification ? (
            <OTPVerification
              onSuccess={() => navigate(ROUTES.HOME)}
              email={getValues().email}
              otpType={OTP_TYPE.EMAIL}
            />
          ) : (
            <Fragment>
              <Box p={2.5}>
                <LoginTitle>Enter the details to continue.</LoginTitle>
                <form onSubmit={handleSubmit(doLogin)}>
                  <Controller
                    control={control}
                    name="email"
                    rules={{ ...FORM_VALIDATIONS.REQUIRED_EMAIL }}
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <CustomTextField
                        variant="standard"
                        placeholder="Enter your email address"
                        fullWidth
                        onChange={onChange}
                        error={!!error}
                        value={watch("email")}
                        helperText={error?.message ?? ""}
                        autoComplete="off"
                      />
                    )}
                  />
                  <Box p={1.8} />
                  <Controller
                    control={control}
                    name="password"
                    rules={{ ...FORM_VALIDATIONS.LOGIN_PWD }}
                    render={({
                      field: { onChange },
                      fieldState: { error },
                    }) => (
                      <CustomTextField
                        variant="standard"
                        placeholder="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        value={watch("password")}
                        autoComplete="off"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <VisibilityOff htmlColor="#9C9C9C" />
                                ) : (
                                  <Visibility htmlColor="#9C9C9C" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        onChange={onChange}
                        error={!!error}
                        helperText={error?.message ?? ""}
                      />
                    )}
                  />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    my={1}
                  >
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        sx={{ marginLeft: "-10px" }}
                        size="small"
                        inputProps={{ "aria-label": "Checkbox demo" }}
                        checked={rememberMe}
                        onChange={(_e, c) => setRememberMe(c)}
                        disabled={
                          watch("password")?.length == 0 ||
                          watch("email")?.length == 0
                        }
                      />
                      <Typography sx={{ fontSize: "12px" }}>
                        Remember me
                      </Typography>
                    </Box>

                    <Typography
                      color="#6ca0ff"
                      fontSize={12}
                      fontWeight={500}
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                    >
                      Forgot password?
                    </Typography>
                  </Stack>

                  {signInFormError && (
                    <Alert style={{ marginBottom: "1rem" }} severity="error">
                      {signInFormError}
                    </Alert>
                  )}

                  <Button
                    color="primary"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      fontWeight: 400,
                      fontSize: 18,
                      marginTop: signInFormError ? 0 : 5,
                    }}
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    endIcon={isSubmitting && <CircularProgress size={15} />}
                  >
                    Log in
                  </Button>
                </form>
                <Typography
                  color="#565656"
                  sx={{ textAlign: "center" }}
                  fontSize={14}
                  mt={2}
                  fontWeight={600}
                >
                  New here?{" "}
                  <Typography
                    component="a"
                    color="primary"
                    fontSize={14}
                    mt={1}
                    fontWeight={600}
                    onClick={goToSignUp}
                    sx={{ cursor: "pointer" }}
                  >
                    Sign up for free
                  </Typography>
                </Typography>
              </Box>

              <Divider sx={{ mt: 3, width: "100%", borderBottomWidth: 6 }} />

              <Box
                p={3}
                display="flex"
                justifyContent="space-between"
                flexDirection="row"
                alignItems="center"
              >
                <Typography color="#565656" fontWeight={400} fontSize={13}>
                  Or Continue with
                </Typography>
                <Box>
                  <SignInOptions />
                </Box>
              </Box>
            </Fragment>
          )}
        </RootBox>
      </Stack>
    </Box>
  );
};

export default LoginForm;
